import React, { useEffect, useState, useRef } from 'react';
import { Card, CardBody, Row, Col } from 'reactstrap';
import { Settings, Database } from 'react-feather';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import AsyncSelect from 'react-select/async';
import Loader from 'react-loader-spinner';

import SitesTable from './SitesTable';
import SitesGraph from './SitesGraph';

const cleanText = (text) => (text || '').replaceAll('_', ' ');

const InfoTile = ({ label, value }) => (
  <div className='py-2'>
    <div
      className='text-muted'
      style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}
    >
      {label}
    </div>
    <div className='text-dark fw-semibold' style={{ fontSize: 14, marginTop: 2 }}>
      {value || '—'}
    </div>
  </div>
);

const tableStyles = {
  headRow: { style: { borderBottom: '1px solid #e9ecef', minHeight: '40px' } },
  headCells: {
    style: {
      fontSize: '11px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      color: '#6c757d',
    },
  },
  rows: { style: { minHeight: '42px', fontSize: '13px' } },
};

export default function SitegroupDetailsInSites({ orgName, current_organization }) {
  const [sitegroupNames, setSitegroupNames] = useState([]);
  const [siteNames, setSiteNames] = useState([]);
  const [sitegroupId, setSitegroupId] = useState('');
  const [loading, setLoading] = useState(true);
  const [sitegroupData, setSitegroupData] = useState({});
  const [sitegroupNamesDropdownOptions, setSitegroupNamesDropdownOptions] = useState([]);
  const [selectedSitegroup, setSelectedSitegroup] = useState({ value: '', label: 'Select a Site Group' });
  const [country, setCountry] = useState('');

  const sitesTableRef = useRef();
  const sitesGraphRef = useRef();

  const handleSitegroup = (e) => {
    setSelectedSitegroup(e);
    const sitegroupVal = e['value'];
    setSitegroupId(sitegroupVal);
    getSitegroupDetails(sitegroupVal);
    sitesTableRef.current?.getSitesBySitegroupId(sitegroupVal);
    sitesGraphRef.current?.fetchSitesGraphData(sitegroupVal);
  };

  useEffect(() => {
    if (orgName) {
      setLoading(true);
      axios
        .get(`/api/v1/organizations/${orgName}/sitegroups?from=site_list&page_type=client&controller_name=sitegroups`)
        .then((res) => {
          setSitegroupNames(res.data.sitegroups);
          setSiteNames(res.data.sites);
          setSitegroupNamesDropdownOptions(convertToArrayOfObjects(res?.data?.sitegroups));
          setCountry(res.data.currency_code);
          setLoading(false);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message);
          setLoading(false);
        });
    }
  }, [orgName]);

  function getSitegroupDetails(id) {
    if (orgName && id !== '') {
      setLoading(true);
      axios
        .get(`/api/v1/organizations/${orgName}/sitegroups/${id}?page_type=client&controller_name=sitegroups`)
        .then((res) => {
          setSitegroupData(res.data.sitegroup);
          setLoading(false);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message);
          setLoading(false);
        });
    }
  }

  const schemaColumns = [
    { name: 'Field Name', selector: 'field_name', sortable: true, grow: 1.5 },
    { name: 'Data Type', selector: 'data_type', sortable: true, width: '120px' },
    { name: 'Mandatory', selector: 'is_mandatory', sortable: true, width: '110px' },
    { name: 'Default Value', selector: 'default_value', sortable: true },
    { name: 'Sample Value', selector: 'sample_value', sortable: true },
    { name: 'Comments', selector: 'comments', sortable: true, grow: 1.5 },
  ];

  function convertToArrayOfObjects(inputArray) {
    return (inputArray || []).map(([value, label]) => ({ value, label }));
  }

  const filterValues = (inputValue) =>
    sitegroupNamesDropdownOptions.filter((i) =>
      i.label.toLowerCase().includes(inputValue.toLowerCase())
    );

  const loadOptions = (inputValue, callback) => {
    setTimeout(() => {
      callback(filterValues(inputValue));
    }, 250);
  };

  const additional = sitegroupData.additional_details || {};
  const hasSitegroupSelected = !!sitegroupData?.id || !!sitegroupData?.fields_to_extract;

  return (
    <>
      {/* Sitegroup selector + configuration overview */}
      <Card className='shadow-sm border-0 mb-3'>
        <CardBody style={{ padding: 24 }}>
          <div className='d-flex align-items-start mb-3'>
            <div
              className='d-inline-flex align-items-center justify-content-center me-3 flex-shrink-0'
              style={{ width: 40, height: 40, borderRadius: 10, background: '#0d6efd15', color: '#0d6efd' }}
            >
              <Settings size={20} />
            </div>
            <div className='flex-grow-1' style={{ minWidth: 0 }}>
              <h6 className='fw-semibold mb-1 text-dark' style={{ fontSize: 16 }}>
                Site Group Configuration
              </h6>
              <p className='text-muted mb-0' style={{ fontSize: 13 }}>
                Pick a site group to view its delivery settings, fields, and schema.
              </p>
            </div>
          </div>

          <div className='mb-3' style={{ maxWidth: 420 }}>
            <label
              className='text-muted d-block mb-1'
              style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}
            >
              Site Group
            </label>
            <AsyncSelect
              name='site_name'
              id='siteName'
              value={selectedSitegroup}
              placeholder='Select a site group…'
              cacheOptions
              defaultOptions={sitegroupNamesDropdownOptions}
              onChange={(res) => handleSitegroup(res)}
              loadOptions={loadOptions}
            />
          </div>

          {loading ? (
            <Loader type='Puff' color='#0d6efd' height={50} width={50} className='pc-loader' />
          ) : (
            hasSitegroupSelected && (
              <Row className='g-3 pt-2' style={{ borderTop: '1px solid #f1f3f5' }}>
                <Col xs={6} md={4} lg={3}>
                  <InfoTile label='Output Format' value={cleanText(sitegroupData.delivery_format)} />
                </Col>
                <Col xs={6} md={4} lg={3}>
                  <InfoTile label='Frequency' value={cleanText(sitegroupData.delivery_frequency_in_text)} />
                </Col>
                <Col xs={6} md={4} lg={3}>
                  <InfoTile label='Delivery Method' value={cleanText(sitegroupData.delivery_method)} />
                </Col>
                <Col xs={6} md={4} lg={3}>
                  <InfoTile label='Merge Files' value={additional.merge_files ? 'Enabled' : 'Disabled'} />
                </Col>
                <Col xs={6} md={4} lg={3}>
                  <InfoTile label='Image Downloads' value={additional.image_downloads ? 'Enabled' : 'Disabled'} />
                </Col>
                {additional.hosted_indexing !== undefined && (
                  <>
                    <Col xs={6} md={4} lg={3}>
                      <InfoTile label='Hosted Indexing' value={additional.hosted_indexing ? 'Enabled' : 'Disabled'} />
                    </Col>
                    <Col xs={6} md={4} lg={3}>
                      <InfoTile
                        label='Two-day Delivery'
                        value={additional.two_days_of_delivery ? 'Enabled' : 'Disabled'}
                      />
                    </Col>
                  </>
                )}
                {sitegroupData.fields_to_extract && (
                  <Col xs={12}>
                    <InfoTile label='Fields to Extract' value={sitegroupData.fields_to_extract} />
                  </Col>
                )}
              </Row>
            )
          )}
        </CardBody>
      </Card>

      {/* Schema field details */}
      {sitegroupData.schema_field_details && sitegroupData.schema_field_details.length > 0 && (
        <Card className='shadow-sm border-0 mb-3'>
          <CardBody style={{ padding: 24 }}>
            <div className='d-flex align-items-start mb-3'>
              <div
                className='d-inline-flex align-items-center justify-content-center me-3 flex-shrink-0'
                style={{ width: 40, height: 40, borderRadius: 10, background: '#19875415', color: '#198754' }}
              >
                <Database size={20} />
              </div>
              <div>
                <h6 className='fw-semibold mb-1 text-dark' style={{ fontSize: 16 }}>
                  Schema Fields
                </h6>
                <p className='text-muted mb-0' style={{ fontSize: 13 }}>
                  Fields delivered for sites in this group.
                </p>
              </div>
            </div>
            <DataTable
              data={sitegroupData.schema_field_details}
              columns={schemaColumns}
              pagination
              responsive
              noHeader
              customStyles={tableStyles}
            />
          </CardBody>
        </Card>
      )}

      <SitesTable
        orgName={orgName}
        current_organization={current_organization}
        sitegroupNames={sitegroupNames}
        siteNames={siteNames}
        country={country}
        ref={sitesTableRef}
      />
      <SitesGraph orgName={orgName} sitegroupId={sitegroupId} ref={sitesGraphRef} />
    </>
  );
}
